import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getLead, updateLead, getProfiles, getLeadActivity, addLeadActivity, getLeadTasks, createTask, updateTask, convertLeadToPatient, fullNameLastFirst } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import useRecordNav from '../hooks/useRecordNav';
import RecordNav from '../components/RecordNav';
import { formatDistanceToNow, format } from 'date-fns';
import ActivityNote from '../components/ActivityNote';

const ACTIVE_STATUSES = ['New Lead', 'Contacted', 'Interested', 'Follow Up'];
const CLOSED_STATUSES = ['Not Interested', 'Unreachable', 'Duplicate', 'Deceased', 'Ineligible', 'Moved / Outside Service Area', 'Chose Another Provider', 'Other'];
const ALL_STATUSES = [...ACTIVE_STATUSES, ...CLOSED_STATUSES];

const GEORGIA_COUNTIES = ['Appling','Atkinson','Bacon','Baker','Baldwin','Banks','Barrow','Bartow','Ben Hill','Berrien','Bibb','Bleckley','Brantley','Brooks','Bryan','Bulloch','Burke','Butts','Calhoun','Camden','Candler','Carroll','Catoosa','Charlton','Chatham','Chattahoochee','Chattooga','Cherokee','Clarke','Clay','Clayton','Clinch','Cobb','Coffee','Colquitt','Columbia','Cook','Coweta','Crawford','Crisp','Dade','Dawson','Decatur','DeKalb','Dodge','Dooly','Dougherty','Douglas','Early','Echols','Effingham','Elbert','Emanuel','Evans','Fannin','Fayette','Floyd','Forsyth','Franklin','Fulton','Gilmer','Glascock','Glynn','Gordon','Grady','Greene','Gwinnett','Habersham','Hall','Hancock','Haralson','Harris','Hart','Heard','Henry','Houston','Irwin','Jackson','Jasper','Jeff Davis','Jefferson','Jenkins','Johnson','Jones','Lamar','Lanier','Laurens','Lee','Liberty','Lincoln','Long','Lowndes','Lumpkin','McDuffie','McIntosh','Macon','Madison','Marion','Meriwether','Miller','Mitchell','Monroe','Montgomery','Morgan','Murray','Muscogee','Newton','Oconee','Oglethorpe','Paulding','Peach','Pickens','Pierce','Pike','Polk','Pulaski','Putnam','Quitman','Rabun','Randolph','Richmond','Rockdale','Schley','Screven','Seminole','Spalding','Stephens','Stewart','Sumter','Talbot','Taliaferro','Tattnall','Taylor','Telfair','Terrell','Thomas','Tift','Toombs','Towns','Treutlen','Troup','Turner','Twiggs','Union','Upson','Walker','Walton','Ware','Warren','Washington','Wayne','Webster','Wheeler','White','Whitfield','Wilcox','Wilkes','Wilkinson','Worth'];

const REFERRAL_SOURCES = ['Doctor / Physician','Hospital','Family / Friend','Social Media','Website','Community Event','DFCS / State Agency','School','Previous Client','Other'];

const ACTIVITY_TYPES = ['Call','Email','Text','In-Person Meeting','Follow Up','Note','Other'];

const statusBadge = (status) => {
  if (status === 'New Lead') return { bg: 'var(--zone-marketing-bg)', color: 'var(--zone-marketing)' };
  if (status === 'Contacted') return { bg: 'var(--zone-processing-bg)', color: 'var(--zone-processing)' };
  if (status === 'Interested') return { bg: 'var(--zone-approval-bg)', color: 'var(--zone-approval)' };
  if (status === 'Follow Up') return { bg: 'var(--zone-active-bg)', color: 'var(--zone-active)' };
  if (status === 'Converted') return { bg: 'var(--success-bg)', color: 'var(--success)' };
  return { bg: 'var(--danger-bg)', color: 'var(--text3)' };
};

export default function LeadProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const recordNav = useRecordNav({ table: 'leads', id, base: '/leads', stateKey: 'leadIds' });
  const { profile: currentUser } = useAuth();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Activity
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [logForm, setLogForm] = useState({ activity_type: 'Call', notes: '', new_status: '' });
  const [logSaving, setLogSaving] = useState(false);
  const [logError, setLogError] = useState('');

  // Tasks
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', priority: 'Normal', due_date: '', assigned_to_id: '' });
  const [taskSaving, setTaskSaving] = useState(false);
  const [taskError, setTaskError] = useState('');

  // Convert
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState('');

  // Close
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeReason, setCloseReason] = useState('Not Interested');
  const [closeNote, setCloseNote] = useState('');
  const [closeSaving, setCloseSaving] = useState(false);

  const loadLead = useCallback(async () => {
    setLoading(true);
    const { data } = await getLead(id);
    setLead(data);
    setEditForm(data || {});
    setLoading(false);
  }, [id]);

  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    const { data } = await getLeadActivity(id);
    setActivity(data || []);
    setActivityLoading(false);
  }, [id]);

  const loadTasks = useCallback(async () => {
    setTasksLoading(true);
    const { data } = await getLeadTasks(id);
    setTasks(data || []);
    setTasksLoading(false);
  }, [id]);

  useEffect(() => { loadLead(); }, [loadLead]);
  useEffect(() => { getProfiles().then(({ data }) => setStaff(data || [])); }, []);
  useEffect(() => { if (tab === 'activity') loadActivity(); }, [tab, loadActivity]);
  useEffect(() => { if (tab === 'tasks') loadTasks(); }, [tab, loadTasks]);

  const handleSaveEdit = async () => {
    setEditSaving(true);
    const staffMember = staff.find(s => s.id === editForm.assigned_to_id);
    const { error } = await updateLead(id, {
      ...editForm,
      assigned_to_id: editForm.assigned_to_id || null,
      assigned_to_name: staffMember?.full_name || editForm.assigned_to_name || null,
      date_of_birth: editForm.date_of_birth || null,
    });
    setEditSaving(false);
    if (!error) { setEditing(false); loadLead(); }
  };

  const handleLogActivity = async (e) => {
    e.preventDefault();
    setLogError('');
    if (!logForm.notes.trim()) { setLogError('Notes are required.'); return; }
    setLogSaving(true);
    const updates = {
      last_activity_date: new Date().toISOString(),
      last_activity_note: logForm.notes,
    };
    if (logForm.new_status) updates.current_status = logForm.new_status;

    const { error: updateError } = await updateLead(id, updates);
    if (updateError) { setLogError(updateError.message); setLogSaving(false); return; }

    const { error: logError_ } = await addLeadActivity({
      lead_id: id,
      activity_type: logForm.activity_type,
      notes: logForm.notes,
      new_status: logForm.new_status || null,
      previous_status: lead.current_status,
      performed_by_name: currentUser?.full_name,
      performed_by_id: currentUser?.id,
    });
    setLogSaving(false);
    if (logError_) { setLogError(logError_.message); return; }
    setShowLogModal(false);
    setLogForm({ activity_type: 'Call', notes: '', new_status: '' });
    loadLead();
    if (tab === 'activity') loadActivity();
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setTaskError('');
    if (!taskForm.title.trim()) { setTaskError('Task title is required.'); return; }
    setTaskSaving(true);
    const assignedStaff = staff.find(s => s.id === taskForm.assigned_to_id);
    const { error } = await createTask({
      title: taskForm.title,
      priority: taskForm.priority || 'Normal',
      due_date: taskForm.due_date || null,
      status: 'Open',
      assigned_to_id: taskForm.assigned_to_id || null,
      assigned_to_name: assignedStaff?.full_name || null,
      lead_id: id,
      created_by_id: currentUser?.id,
      created_by_name: currentUser?.full_name,
    });
    setTaskSaving(false);
    if (error) { setTaskError(error.message); return; }
    setShowTaskModal(false);
    setTaskForm({ title: '', priority: 'Normal', due_date: '', assigned_to_id: '' });
    loadTasks();
  };

  const handleToggleTask = async (task) => {
    const newStatus = task.status === 'Done' ? 'Open' : 'Done';
    // completed_at, as on the Tasks page: the daily digest's "completed
    // yesterday" list reads it.
    await updateTask(task.id, { status: newStatus, completed_at: newStatus === 'Done' ? new Date().toISOString() : null });
    loadTasks();
  };

  const handleCloseLead = async () => {
    setCloseSaving(true);
    await updateLead(id, { current_status: closeReason, last_activity_note: closeNote || null });
    setCloseSaving(false);
    setShowCloseModal(false);
    setCloseNote('');
    loadLead();
  };

  const handleConvert = async () => {
    if (!window.confirm(`Convert ${lead.first_name} ${lead.last_name} to a Client? This will move them to the Outreach stage of the client pipeline.`)) return;
    setConverting(true);
    setConvertError('');
    const { data, error } = await convertLeadToPatient(id, currentUser);
    setConverting(false);
    if (error) { setConvertError(error.message || String(error)); return; }
    navigate(`/clients/${data.client.id}`);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;
  if (!lead) return <div className="empty-state"><p>Lead not found.</p></div>;

  const badge = statusBadge(lead.current_status);
  const isConverted = lead.current_status === 'Converted';
  const isClosed = CLOSED_STATUSES.includes(lead.current_status) || isConverted;
  const ef = editForm;

  return (
    <div>
      {/* Convert banner */}
      {!isClosed && (
        <div style={{ background: 'var(--success-bg)', border: '1px solid var(--success-border)', borderRadius: 10, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: 'var(--success)', fontSize: 13 }}>✓ Ready to convert this lead to a client?</span>
          {convertError && <span style={{ color: 'var(--danger)', fontSize: 12 }}>{convertError}</span>}
          <button className="btn btn-sm" style={{ marginLeft: 'auto', background: 'var(--success)', color: '#fff', border: 'none' }} onClick={handleConvert} disabled={converting}>
            {converting ? 'Converting...' : 'Convert to Client →'}
          </button>
        </div>
      )}

      {isConverted && lead.converted_client_id && (
        <div style={{ background: 'var(--accent-bg)', border: '1px solid var(--accent)', borderRadius: 10, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: 'var(--accent)', fontSize: 13 }}>This lead has been converted to a client.</span>
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => navigate(`/clients/${lead.converted_client_id}`)}>View Client →</button>
        </div>
      )}

      {/* Navigation bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => navigate('/leads')}>Leads</span>
          <span>/</span>
          <span>{lead.last_name}, {lead.first_name}</span>
        </span>
        <div style={{ flex: 1 }} />
        <RecordNav {...recordNav} label="lead" />
      </div>

      {/* Header */}
      <div className="card" style={{ marginBottom: 16, padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--zone-active)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 600, color: '#fff', flexShrink: 0 }}>
            {lead.first_name?.[0]}{lead.last_name?.[0]}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{lead.last_name}, {lead.first_name}</h2>
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: badge.bg, color: badge.color, fontWeight: 600 }}>{lead.current_status}</span>
              <span className="td-mono" style={{ fontSize: 12, color: 'var(--text3)' }}>{lead.lead_id}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {lead.county && <span>📍 {lead.county}</span>}
              {lead.phone && <span>📞 {lead.phone}</span>}
              {lead.referral_source && <span>🔗 {lead.referral_source}</span>}
              {lead.assigned_to_name && <span>👤 {lead.assigned_to_name}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline btn-sm" onClick={() => setShowLogModal(true)}>Log Activity</button>
            <button className="btn btn-outline btn-sm" onClick={() => setEditing(!editing)}>{editing ? 'Cancel' : 'Edit'}</button>
            {!isClosed && (
              <button className="btn btn-outline btn-sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger-border)' }}
                onClick={() => { setShowCloseModal(true); setCloseReason('Not Interested'); setCloseNote(''); }}>
                Close Lead
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 16 }}>
        {['overview', 'activity', 'tasks'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '7px 18px', fontSize: 12, border: 'none', cursor: 'pointer', borderRadius: 8, fontWeight: tab === t ? 600 : 400, background: tab === t ? 'var(--accent)' : 'var(--card-bg)', color: tab === t ? '#fff' : 'var(--text2)', transition: 'all 0.15s' }}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === 'tasks' && tasks.length > 0 && <span style={{ marginLeft: 5, background: 'rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 5px', fontSize: 10 }}>{tasks.length}</span>}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="card">
            <div className="card-header"><div className="card-title">Contact Info</div></div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {editing ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div className="form-group" style={{ margin: 0 }}><label className="form-label">First Name</label><input className="form-input" value={ef.first_name || ''} onChange={e => setEditForm(p => ({ ...p, first_name: e.target.value }))} /></div>
                    <div className="form-group" style={{ margin: 0 }}><label className="form-label">Last Name</label><input className="form-input" value={ef.last_name || ''} onChange={e => setEditForm(p => ({ ...p, last_name: e.target.value }))} /></div>
                    <div className="form-group" style={{ margin: 0 }}><label className="form-label">Phone</label><input className="form-input" value={ef.phone || ''} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} /></div>
                    <div className="form-group" style={{ margin: 0 }}><label className="form-label">Email</label><input className="form-input" value={ef.email || ''} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} /></div>
                    <div className="form-group" style={{ margin: 0 }}><label className="form-label">Date of Birth</label><input className="form-input" type="date" value={ef.date_of_birth || ''} onChange={e => setEditForm(p => ({ ...p, date_of_birth: e.target.value }))} /></div>
                    <div className="form-group" style={{ margin: 0 }}><label className="form-label">Guardian Name</label><input className="form-input" value={ef.guardian_name || ''} onChange={e => setEditForm(p => ({ ...p, guardian_name: e.target.value }))} /></div>
                    <div className="form-group" style={{ margin: 0 }}><label className="form-label">Guardian Phone</label><input className="form-input" value={ef.guardian_phone || ''} onChange={e => setEditForm(p => ({ ...p, guardian_phone: e.target.value }))} /></div>
                  </div>
                </>
              ) : (
                <>
                  {[['Phone', lead.phone], ['Email', lead.email], ['Date of Birth', lead.date_of_birth], ['Guardian', lead.guardian_name], ['Guardian Phone', lead.guardian_phone]].map(([label, val]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ color: 'var(--text3)' }}>{label}</span>
                      <span style={{ color: 'var(--text)', fontWeight: 500 }}>{val || '—'}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><div className="card-title">Lead Details</div></div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {editing ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Status</label>
                    <select className="form-select" value={ef.current_status || ''} onChange={e => setEditForm(p => ({ ...p, current_status: e.target.value }))}>
                      {ALL_STATUSES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">County</label>
                    <select className="form-select" value={ef.county || ''} onChange={e => setEditForm(p => ({ ...p, county: e.target.value }))}>
                      <option value="">Select county</option>
                      {GEORGIA_COUNTIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Referral Source</label>
                    <select className="form-select" value={ef.referral_source || ''} onChange={e => setEditForm(p => ({ ...p, referral_source: e.target.value }))}>
                      <option value="">Select source</option>
                      {REFERRAL_SOURCES.map(r => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Assign To</label>
                    <select className="form-select" value={ef.assigned_to_id || ''} onChange={e => setEditForm(p => ({ ...p, assigned_to_id: e.target.value }))}>
                      <option value="">Unassigned</option>
                      {staff.map(s => <option key={s.id} value={s.id}>{fullNameLastFirst(s.full_name)}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                    <label className="form-label">Notes</label>
                    <textarea className="form-textarea" rows={3} value={ef.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} />
                  </div>
                </div>
              ) : (
                <>
                  {[['County', lead.county], ['Referral Source', lead.referral_source], ['Assigned To', lead.assigned_to_name], ['Added', lead.created_at ? format(new Date(lead.created_at), 'MMM d, yyyy') : null], ['Added By', lead.created_by_name]].map(([label, val]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ color: 'var(--text3)' }}>{label}</span>
                      <span style={{ color: 'var(--text)', fontWeight: 500 }}>{val || '—'}</span>
                    </div>
                  ))}
                  {lead.notes && (
                    <div style={{ marginTop: 4, padding: '8px 10px', background: 'var(--bg)', borderRadius: 8, fontSize: 12, color: 'var(--text2)' }}>{lead.notes}</div>
                  )}
                </>
              )}
            </div>
            {editing && (
              <div style={{ padding: '0 16px 16px', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(false); setEditForm(lead); }}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={handleSaveEdit} disabled={editSaving}>{editSaving ? 'Saving...' : 'Save Changes'}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Activity Tab */}
      {tab === 'activity' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Activity Log</div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowLogModal(true)}>+ Log Activity</button>
          </div>
          {activityLoading ? (
            <div style={{ padding: 40, textAlign: 'center' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
          ) : activity.length === 0 ? (
            <div className="empty-state"><p>No activity logged yet.</p></div>
          ) : (
            <div style={{ padding: '0 16px 16px' }}>
              {activity.map((entry, i) => (
                <div key={entry.id} style={{ display: 'flex', gap: 12, paddingTop: i === 0 ? 16 : 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--zone-active)', marginTop: 5 }} />
                    {i < activity.length - 1 && <div style={{ width: 2, flex: 1, background: 'var(--border)', margin: '4px 0' }} />}
                  </div>
                  <div style={{ paddingBottom: 16, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{entry.activity_type}</span>
                      {entry.new_status && (
                        <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 8, background: 'var(--accent-bg)', color: 'var(--accent)' }}>→ {entry.new_status}</span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' }}>
                        {entry.created_at ? formatDistanceToNow(new Date(entry.created_at), { addSuffix: true }) : ''}
                      </span>
                    </div>
                    <ActivityNote item={entry} size={12} onSaved={loadActivity} />
                    {entry.performed_by_name && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>by {entry.performed_by_name}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tasks Tab */}
      {tab === 'tasks' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Tasks</div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowTaskModal(true)}>+ Add Task</button>
          </div>
          {tasksLoading ? (
            <div style={{ padding: 40, textAlign: 'center' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
          ) : tasks.length === 0 ? (
            <div className="empty-state"><p>No tasks yet.</p></div>
          ) : (
            <div style={{ padding: '8px 16px 16px' }}>
              {tasks.map(task => (
                <div key={task.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <input type="checkbox" checked={task.status === 'Done'} onChange={() => handleToggleTask(task)} style={{ marginTop: 3, accentColor: 'var(--accent)', cursor: 'pointer' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: task.status === 'Done' ? 'var(--text3)' : 'var(--text)', textDecoration: task.status === 'Done' ? 'line-through' : 'none' }}>{task.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                      {task.due_date && <span>Due {format(new Date(task.due_date + 'T00:00:00'), 'MMM d, yyyy')} · </span>}
                      {task.assigned_to_name && <span>→ {task.assigned_to_name}</span>}
                    </div>
                  </div>
                  <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 8, background: task.priority === 'High' ? 'var(--danger-bg)' : task.priority === 'Low' ? 'var(--success-bg)' : 'var(--accent-bg)', color: task.priority === 'High' ? 'var(--danger)' : task.priority === 'Low' ? 'var(--success)' : 'var(--accent)' }}>{task.priority}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Log Activity Modal */}
      {showLogModal && (
        <div className="modal-overlay" onClick={() => setShowLogModal(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Log Activity</div>
              <button className="modal-close" onClick={() => setShowLogModal(false)}>✕</button>
            </div>
            <form onSubmit={handleLogActivity}>
              <div className="modal-body">
                {logError && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{logError}</div>}
                <div className="form-group">
                  <label className="form-label">Activity Type</label>
                  <select className="form-select" value={logForm.activity_type} onChange={e => setLogForm(p => ({ ...p, activity_type: e.target.value }))}>
                    {ACTIVITY_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Update Status (optional)</label>
                  <select className="form-select" value={logForm.new_status} onChange={e => setLogForm(p => ({ ...p, new_status: e.target.value }))}>
                    <option value="">Keep current ({lead.current_status})</option>
                    {ALL_STATUSES.filter(s => s !== lead.current_status).map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes *</label>
                  <textarea className="form-textarea" rows={4} value={logForm.notes} onChange={e => setLogForm(p => ({ ...p, notes: e.target.value }))} placeholder="What happened on this contact?" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowLogModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={logSaving}>{logSaving ? 'Saving...' : 'Log Activity'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      {showTaskModal && (
        <div className="modal-overlay" onClick={() => setShowTaskModal(false)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Add Task</div>
              <button className="modal-close" onClick={() => setShowTaskModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreateTask}>
              <div className="modal-body">
                {taskError && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{taskError}</div>}
                <div className="form-group">
                  <label className="form-label">Task *</label>
                  <input className="form-input" value={taskForm.title} onChange={e => setTaskForm(p => ({ ...p, title: e.target.value }))} placeholder="What needs to be done?" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Priority</label>
                    <select className="form-select" value={taskForm.priority} onChange={e => setTaskForm(p => ({ ...p, priority: e.target.value }))}>
                      <option>Low</option><option>Normal</option><option>High</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Due Date</label>
                    <input className="form-input" type="date" value={taskForm.due_date} onChange={e => setTaskForm(p => ({ ...p, due_date: e.target.value }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Assign To</label>
                  <select className="form-select" value={taskForm.assigned_to_id} onChange={e => setTaskForm(p => ({ ...p, assigned_to_id: e.target.value }))}>
                    <option value="">Unassigned</option>
                    {staff.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowTaskModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={taskSaving}>{taskSaving ? 'Saving...' : 'Add Task'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Close Lead Modal */}
      {showCloseModal && (
        <div className="modal-overlay" onClick={() => setShowCloseModal(false)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Close Lead</div>
              <button className="modal-close" onClick={() => setShowCloseModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
                Closing <strong>{lead.first_name} {lead.last_name}</strong> will move them to closed status. You can reactivate by editing their status later.
              </p>
              <div className="form-group">
                <label className="form-label">Reason</label>
                <select className="form-select" value={closeReason} onChange={e => setCloseReason(e.target.value)}>
                  <option>Not Interested</option>
                  <option>Unreachable</option>
                  <option>Duplicate</option>
                  <option>Deceased</option>
                  <option>Ineligible</option>
                  <option>Moved / Outside Service Area</option>
                  <option>Chose Another Provider</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Note (optional)</label>
                <textarea className="form-textarea" rows={2} value={closeNote} onChange={e => setCloseNote(e.target.value)} placeholder="Add a closing note..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowCloseModal(false)}>Cancel</button>
              <button className="btn btn-sm" style={{ background: 'var(--danger)', color: '#fff', border: 'none' }} disabled={closeSaving} onClick={handleCloseLead}>{closeSaving ? 'Closing…' : 'Close Lead'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
